/**
 * CharacterCard Web Component
 *
 * @description
 * Displays a character card for both character selection and gameplay modes.
 *
 * @extends HTMLElement
 *
 * @example
 * <character-card
 *   character-name="Human Fighter"
 *   character-slug="human-fighter"
 *   health="100"
 *   weapon="true"
 *   shield="true"
 *   mode="selector"
 *   selectable="true">
 * </character-card>
 */

class CharacterCard extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this._stylesLoaded = false;
  }

  static get observedAttributes() {
    return [
      'character-name',
      'character-slug',
      'character-icon',
      'player-name',
      'health',
      'starting-health',
      'weapon',
      'shield',
      'weapon-name',
      'shield-name',
      'description',
      'mode',
      'selectable',
      'current-move',
      'current-move-range',
      'total-score',
      'hit'
    ];
  }

  async connectedCallback() {
    await this._loadStyles();
    this.render();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue) {
      this.render();
    }
  }

  async _loadStyles() {
    if (this._stylesLoaded) return;

    try {
      const response = await fetch('/assets/styles/styles.css');
      const css = await response.text();
      const style = document.createElement('style');
      style.textContent = css;
      this.shadowRoot.appendChild(style);
      this._stylesLoaded = true;
    } catch (error) {
      console.error('Failed to load styles for character-card:', error);
    }
  }

  getHealthPercentage() {
    const health = parseInt(this.getAttribute('health') || '100');
    const startingHealth = parseInt(this.getAttribute('starting-health') || '100');
    return Math.max(0, Math.min(100, (health / startingHealth) * 100));
  }

  getHealthBarColor() {
    const percentage = this.getHealthPercentage();
    if (percentage > 66) return 'bg-success';
    if (percentage > 33) return 'bg-warning';
    return 'bg-danger';
  }

  // Public methods for updating game state
  updateRestrictions(restrictions) {
    const restrictionsList = this.shadowRoot.querySelector('.restrictions-list');
    if (!restrictionsList) return;

    restrictionsList.innerHTML = '';
    restrictions.forEach(restriction => {
      const li = document.createElement('li');
      li.className = 'restriction-item';
      li.textContent = restriction;
      restrictionsList.appendChild(li);
    });
  }

  updateBonuses(bonuses) {
    const bonusesList = this.shadowRoot.querySelector('.bonuses-list');
    if (!bonusesList) return;

    bonusesList.innerHTML = '';
    bonuses.forEach(bonus => {
      const li = document.createElement('li');
      li.className = 'bonus-item';
      li.textContent = bonus;
      bonusesList.appendChild(li);
    });
  }

  updateMiniLog(logText) {
    const miniLog = this.shadowRoot.querySelector('.mini-log');
    if (!miniLog) return;
    miniLog.innerHTML = logText;
  }

  render() {
    const characterName = this.getAttribute('character-name') || 'Unknown Character';
    const characterSlug = this.getAttribute('character-slug') || '';
    const characterIcon = this.getAttribute('character-icon') || '';
    const playerName = this.getAttribute('player-name') || '';
    const health = this.getAttribute('health') || '100';
    const startingHealth = this.getAttribute('starting-health') || '100';
    const weapon = this.getAttribute('weapon') === 'true';
    const shield = this.getAttribute('shield') === 'true';
    const weaponName = this.getAttribute('weapon-name') || '';
    const shieldName = this.getAttribute('shield-name') || '';
    const description = this.getAttribute('description') || '';
    const mode = this.getAttribute('mode') || 'game';
    const selectable = this.getAttribute('selectable') === 'true';
    const currentMove = this.getAttribute('current-move') || '';
    const currentMoveRange = this.getAttribute('current-move-range') || '';
    const totalScore = this.getAttribute('total-score') || '';
    const hit = this.getAttribute('hit') === 'true';

    const healthPercentage = this.getHealthPercentage();
    const healthBarColor = this.getHealthBarColor();

    // Find or create content container (preserve style elements)
    let container = this.shadowRoot.querySelector('.character-card-container');
    if (!container) {
      container = document.createElement('div');
      container.className = 'character-card-container';
      this.shadowRoot.appendChild(container);
    }

    container.innerHTML = `
      <div class="card character-card ${mode}-mode ${selectable ? 'selectable' : ''}">
        <div class="card-decoration"></div>

        <div class="card-header-custom">
          <div class="header-top-line">
            <h3 class="character-name">${characterIcon ? characterIcon + ' ' : ''}${characterName}</h3>
            ${playerName ? `<div class="player-badge">👤 ${playerName}</div>` : mode === 'game' && !selectable ? '<div class="player-badge no-player-name">Waiting for player...</div>' : ''}
            <div class="equipment-header">
              <span class="equipment-item-inline weapon ${weapon ? 'has-item' : 'no-item'}">
                <span class="equipment-icon">🗡️</span>
                <span class="equipment-value">${weapon ? weaponName || 'Armed' : 'None'}</span>
              </span>
              <span class="equipment-item-inline shield ${shield ? 'has-item' : 'no-item'}">
                <span class="equipment-icon">🛡️</span>
                <span class="equipment-value">${shield ? shieldName || 'Armed' : 'None'}</span>
              </span>
            </div>
          </div>
          ${description ? `<div class="description">${description}</div>` : ''}

          <div class="health-bar-container">
            <div class="health-label">
              <span>❤️ Health</span>
              <span class="health-value">${health} / ${startingHealth}</span>
            </div>
            <div class="progress" role="progressbar" aria-label="Health"
                 aria-valuenow="${healthPercentage}" aria-valuemin="0" aria-valuemax="100">
              <div class="progress-bar ${healthBarColor}" style="width: ${healthPercentage}%">
                ${Math.round(healthPercentage)}%
              </div>
            </div>
          </div>
        </div>

        ${mode === 'game' && !selectable ? `
          <div class="card-body-custom">
            <div class="game-info-section">
              ${currentMove ? `
                <div class="current-move ${hit ? 'hit' : ''}">
                  <div class="move-name">🎯 ${currentMove}</div>
                  ${currentMoveRange || totalScore ? `
                    <div class="move-details">
                      ${currentMoveRange ? `Range: ${currentMoveRange}` : ''}
                      ${currentMoveRange && totalScore ? ' • ' : ''}
                      ${totalScore ? `Score: ${totalScore}` : ''}
                    </div>
                  ` : ''}
                </div>
              ` : ''}

              <div class="game-stats">
                <div class="stat-section">
                  <h6>📜 Last Action</h6>
                  <div class="mini-log">Waiting for move...</div>
                </div>

                <div class="stat-section">
                  <h6>🚫 Restrictions</h6>
                  <ul class="restrictions-list"></ul>
                </div>

                <div class="stat-section">
                  <h6>⭐ Bonuses</h6>
                  <ul class="bonuses-list"></ul>
                </div>
              </div>
            </div>
          </div>
        ` : ''}
      </div>
    `;
  }
}

// Add container styles
const containerStyles = `
  <style>
    .character-card-container {
      display: contents;
    }
  </style>
`;


// Register the custom element
if (!customElements.get('character-card')) {
  customElements.define('character-card', CharacterCard);
}

export { CharacterCard };
